// client/src/components/School_Admin/Dashboard/DistributionPie.js
//
// THE ONE PIE the school overview draws three times — students by class,
// teachers by department, staff by role.
//
// WHY ALL THREE ARE PIES. Two of them were bar charts, and bars broke down on
// exactly the axis a school varies along: a bar chart's readability depends on
// every bar fitting one ceiling, so a school with one class of 60 and eleven of
// 8 drew a single full-height column beside eleven stubs, and a school with
// forty classes drew forty columns four pixels wide. Neither is legible, and
// neither gets better as the school grows.
//
// A pie has no axis to outgrow. It answers the question these three are
// actually asked — how is the school split up — as a SHARE, which is scale
// free: the same chart reads the same for 40 pupils and for 4 000.
//
// A pie is a poor instrument for comparing many quantities, so this one ships
// with the mitigations that make it honest: slices sorted largest first, a
// percentage AND the exact count on every legend entry, and the tail past seven
// folded into "Other" rather than given an eighth colour nobody can tell from
// the third. Nothing here asks the reader to compare two angles by eye.
//
// The colours are the ones the phone uses (apps/mobile/src/components/Charts.js
// `categorical`), whose ORDER is a colour-vision safety mechanism rather than
// decoration — every neighbouring pair clears the separation gate, so slices
// must be filled from the first slot downward and never cycled.

import React from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Typography, Box, useTheme } from '@mui/material';

ChartJS.register(ArcElement, Tooltip, Legend);

/** Validated categorical order — see the note above. Do not reorder. */
export const CATEGORICAL = [
  '#1E52A8', // royal 500
  '#C9952A', // gold 500
  '#0A7FAF', // sky 700
  '#166534', // forest 700
  '#881337', // burgundy 700
  '#3430A0', // indigo 600
  '#3DB8EA', // sky 500
  '#92680A', // gold 700
];

/**
 * @param {{label:string, value:number, details?:string[]}[]} items
 * @param {string} emptyText   what to say when there is nothing to draw — never
 *                             an empty circle, which reads as zero rather than
 *                             as "nothing recorded yet"
 * @param {string} unitLabel   what the counts are OF, for the tooltip
 *
 * `details` is an optional list of names shown under the count when a slice is
 * hovered. A count answers "how many" and the tooltip already did; on the
 * teachers-per-class chart the question underneath it is "who", and that cannot
 * be read off an angle. Long lists are capped so the tooltip stays a tooltip.
 */
const DistributionPie = ({ items, emptyText, unitLabel, height = 300, detailsLimit = 12 }) => {
  const theme = useTheme();

  const all = (Array.isArray(items) ? items : [])
    .map((it) => ({
      label: String(it.label ?? '—'),
      value: Number(it.value) || 0,
      details: Array.isArray(it.details) ? it.details.filter(Boolean).map(String) : [],
    }))
    .filter((it) => it.value > 0)
    .sort((a, b) => b.value - a.value);

  if (!all.length) {
    return (
      <Typography variant="body2" sx={{ textAlign: 'center', py: 4 }}>
        {emptyText}
      </Typography>
    );
  }

  // Past seven the tail becomes "Other" rather than a colour the palette has
  // not validated.
  const head = all.slice(0, 7);
  const tail = all.slice(7);
  const rows = tail.length
    ? [...head, {
        label: `Other (${tail.length})`,
        value: tail.reduce((n, x) => n + x.value, 0),
        // The folded tail names what it folded, so the "Other" slice is still
        // answerable rather than a dead end.
        details: tail.map((x) => x.label),
      }]
    : head;

  const chartData = {
    labels: rows.map((r) => r.label),
    datasets: [{
      data: rows.map((r) => r.value),
      backgroundColor: CATEGORICAL.slice(0, rows.length),
      // The gap between neighbouring slices is the page's own colour, so it
      // reads as air rather than as a border drawn around each wedge.
      borderColor: theme.palette.background.paper,
      borderWidth: 2,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { padding: 16, boxWidth: 12, font: { size: 12 } },
      },
      tooltip: {
        callbacks: {
          // Both the count and the share: the share answers "how big a slice is
          // this" and the count answers "how many", and a reader hovering a
          // slice usually wants the second.
          label: (ctx) => {
            const total = ctx.chart.getDatasetMeta(0).total || 1;
            const value = ctx.raw || 0;
            const pct = Math.round((value / total) * 1000) / 10;
            return `${ctx.label}: ${value}${unitLabel ? ` ${unitLabel}` : ''} (${pct}%)`;
          },
          // WHO, under HOW MANY. Returned as an array so chart.js renders one
          // name per line; a single joined string would run off the tooltip.
          afterLabel: (ctx) => {
            const names = rows[ctx.dataIndex]?.details || [];
            if (!names.length) return undefined;
            const shown = names.slice(0, detailsLimit);
            const rest = names.length - shown.length;
            return rest > 0 ? [...shown, `+${rest} more`] : shown;
          },
        },
      },
    },
  };

  return (
    <Box sx={{ height }}>
      <Pie data={chartData} options={options} />
    </Box>
  );
};

export default DistributionPie;
